import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader } from '@components/loader';
import { CompanyRoles, HIDDEN_ROLES, ROLES_ORDER } from '@domain/company';
import { CompanyUserInfo } from '@hooks/company/use-get-company-user-info';
import { useGetCompanyUserList } from '@hooks/company/use-get-company-user-list';

import { ImpersonateUserPayload } from './impersonate-user-modal.type';

export interface ImpersonateRoleCardsProps {
  companyId: string;
  onImpersonate: (payload: ImpersonateUserPayload) => void;
}

const ROLE_FETCH_LIMIT = 100;

const buildRoleToUser = (users: CompanyUserInfo[]): Map<CompanyRoles, CompanyUserInfo> => {
  const byRole = new Map<CompanyRoles, CompanyUserInfo>();
  users.forEach((user) => {
    const roleName = user.role.name;
    if (HIDDEN_ROLES.includes(roleName)) return;
    if (!byRole.has(roleName)) byRole.set(roleName, user);
  });
  return byRole;
};

export const ImpersonateRoleCards = ({ companyId, onImpersonate }: ImpersonateRoleCardsProps) => {
  const { t } = useTranslation();

  const { data, isLoading } = useGetCompanyUserList({
    companyId,
    params: { limit: ROLE_FETCH_LIMIT },
    options: { enabled: !!companyId },
  });

  const roleEntries = useMemo(() => {
    const allUsers = data?.pages.flat() ?? [];
    const byRole = buildRoleToUser(allUsers);
    return ROLES_ORDER.flatMap((role) => {
      const user = byRole.get(role as CompanyRoles);
      return user ? [{ role: role as CompanyRoles, user }] : [];
    });
  }, [data]);

  if (isLoading) {
    return (
      <div className='py-4' data-testid='impersonate-role-cards-loading'>
        <Loader />
      </div>
    );
  }

  if (roleEntries.length === 0) return null;

  return (
    <section data-testid='impersonate-role-cards' className='flex flex-col gap-3 mt-4'>
      <ul className='grid grid-cols-1 sm:grid-cols-2 gap-2'>
        {roleEntries.map(({ role, user }) => (
          <li key={role}>
            <button
              type='button'
              data-testid={`impersonate-role-card-${role}`}
              onClick={() => onImpersonate({ email: user.email, uuid: user.id })}
              className='w-full flex flex-col gap-1 rounded-md border border-extra-soft bg-surface-primary px-4 py-3 text-left hover:border-hard focus:border-hard transition'>
              <span className='text-body-s font-semibold text-primary'>{t(`common:role_${role.toLowerCase()}`)}</span>
              <span className='truncate text-caption text-tertiary'>
                {user.firstName} {user.lastName}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
};
